import React, { useState, useContext, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import toast from 'react-hot-toast';
import CreatableSelect from 'react-select/creatable';
import ReactTooltip from 'react-tooltip';
import { PhoneNumberInput } from '../Input';
import Button from '../Button';
import { CREATE_CONTACTS } from '../../graphql/queries';
import { AppContext } from '../../context/AppContext';
import { SEARCH_TYPES } from '../../util/customersUtil';
import useMutateAnalytics from '../../util/mutate.Analytics';
import analyticsKeys from '../../util/UsageStatLinks';
import { UserContext } from '../../context/UserContext';
import colors from '../../styles/colors';
import Icon from '../Icon';
import { IconEnum } from '../Icons';

const Form = styled.form`
  width: clamp(250px, 75vw, 300px);
  padding: 20px 15px;
`;

const TooltipStyles = createGlobalStyle`
.__react_component_tooltip{
      display: inline-block !important;
      max-width: 300px;
    };
    .css-12jo7m5{
      font-size:13px !important;
    };
    .css-1pahdxg-control{
      border-color: ${colors.grey3} !important;
      box-shadow: none !important;
      min-height: 40px;
    }
    .css-yk16xz-control{
      min-height: 40px !important;
    };
  
`;

const MessageDetails = styled.div`
  display: flex;
  font-size: 1.2rem;
  color: ${colors.grey3};
  padding: 0 5px;
  margin: 10px 0;
`;

const customStyles = {
  option: (provided) => ({
    ...provided,
    fontSize: '13px',

  }),
};

export default function EnterNewContactModal({
  setSearchQuery,
  setReferrers,
  close,
}) {
  const { user, setUser } = useContext(UserContext);
  const { app, setApp } = useContext(AppContext);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const mutateAnalytics = useMutateAnalytics();

  const [createContacts, { loading }] = useMutation(CREATE_CONTACTS, {
    onError(error) {
      console.log(error);
    },
    onCompleted() {
      setApp({ ...app, paginationOffset: 1, searchQuery: null });
      setReferrers([]);
      const oldTagOptions = user.organization.tags;
      const updatedTags = selectedTags.map((tag) => tag.value);

      const newTags = updatedTags.filter((i) => !oldTagOptions.includes(i));
      const sortedTags = [...user.organization.tags, ...newTags].sort(
        (a, b) => {
          if (a > b) {
            return 1;
          }
          return -1;
        },
      );
      user.organization.tags = sortedTags;
      const updatedUser = { ...user };
      setUser(() => updatedUser);
      app.refetchContacts();
      close();
    },
  });

  const isValidContact = (contact) => {
    const numberToSend = contact.replace(/ /g, '').trim() || '';
    if (numberToSend.split('')[0] === '1' && numberToSend.length < 11) {
      return false;
    }
    if (numberToSend.length < 10) {
      return false;
    }
    return numberToSend;
  };

  const setValidateTagValue = (newValue = '') => {
    const regex = /^[a-z0-9-]{1,}$/;
    const isValid = regex?.test(newValue);
    if (isValid) {
      const tagsWithNew = [
        ...selectedTags,
        { label: newValue, value: newValue },
      ];
      setTags((old) => [...old, { label: newValue, value: newValue }] || []);
      return setSelectedTags(tagsWithNew || []);
    }
    toast.error('Please enter a valid contact tag');
  };

  useEffect(() => {
    if (user?.organization?.tags) {
      const tagsData = user?.organization?.tags.map((tag) => {
        return {
          label: tag,
          value: tag,
        };
      });
      setTags(tagsData);
    }
  }, [user]);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        const validNumber = isValidContact(phoneNumber);
        if (validNumber) {
          setSearchQuery({ type: SEARCH_TYPES.Clear });
          createContacts({
            variables: {
              phoneNumber: [validNumber],
              tags: selectedTags.map((i) => i.value),
            },
          });
          const mutateAnalyticsVariables = {
            link: analyticsKeys.CONTACTS_ADD_CONTACTS,
          };
          mutateAnalytics(mutateAnalyticsVariables);
        } else {
          toast.error('Enter a Valid Phone Number');
        }
      }}
    >
      <PhoneNumberInput
        type="tel"
        header="PHONE NUMBER"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
        width="100%"
        required
      />

      <div style={{ marginTop: '0px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '0px',
            width: 'max-content',
          }}
          data-tip="Tags should contain only lower-case letters, numbers, and hyphens (no other special characters or spaces allowed)."
        >
          <MessageDetails>TAGS</MessageDetails>{' '}
          <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
        </div>
        <CreatableSelect
          onChange={(updatedTags) => setSelectedTags(updatedTags || [])}
          options={tags}
          isClearable={false}
          isMulti
          value={selectedTags}
          onCreateOption={setValidateTagValue}
          styles={customStyles}
        />
      </div>

      <Button
        margin="40px 0px 0px 0px"
        text="IMPORT CONTACT"
        type="submit"
        width="100%"
        loading={loading}
      />
      <TooltipStyles />
      <ReactTooltip place="right" type="dark" effect="solid" />
    </Form>
  );
}
